import { Component, inject } from '@angular/core';
import { Select, SelectChangeEvent } from 'primeng/select';
import { AsyncPipe } from '@angular/common';
import { UserService } from 'src/app/shared/services/user.service';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { ISelectOption } from 'src/app/shared/interfaces/primeng.interface';
import { map } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { LOCAL_STORAGE_KEYS } from 'src/app/shared/constants/local-storage.constants';
import { Store } from '@ngxs/store';
import { GetUserRegions } from 'src/app/shared/store/states/user/user.actions';
import { UserState } from 'src/app/shared/store/states/user/user.state';
import { datadogLogs } from '@datadog/browser-logs';
import { RegionDto } from 'src/app/shared/dtos/user.dto';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-region-modal',
  imports: [
    Select,
    AsyncPipe,
    FormsModule,
    TranslocoPipe
  ],
  templateUrl: './region-modal.html',
  styleUrl: './region-modal.scss'
})
export class RegionModal {
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly localStorageService = inject(LocalStorageService);

  protected readonly selectedRegion$ = new BehaviorSubject<RegionDto | undefined>(undefined);
  protected readonly selectedOption$ = new BehaviorSubject<ISelectOption | undefined>(undefined);

  protected readonly regions$: Observable<RegionDto[]> = this.store.dispatch(new GetUserRegions()).pipe(
    switchMap(() => this.store.select(UserState.getUserRegions)),
  );

  protected readonly options$: Observable<ISelectOption[]> = this.regions$.pipe(
    map(regions => {
      return regions.map((region): ISelectOption => {
        if (region.current) {
          this.selectedOption$.next({
            name: region.name,
            code: region.id
          });
          this.selectedRegion$.next(region);
        }
        return {
          name: region.name,
          code: region.id
        };
      });
    })
  );

  protected setSelectedRegion(event: SelectChangeEvent, regions: RegionDto[]): void {
    const selectedRegion = regions.find(region => region.id === event.value.code);
    this.selectedRegion$.next(selectedRegion);
  }

  protected updateRegion(): void {
    const id = this.selectedOption$.value?.code;

    if (id) {
      this.userService.setCurrentUserRegion(String(id)).subscribe({
        next: () => {
          this.localStorageService.removeItem(LOCAL_STORAGE_KEYS.RecentlyViewedItems);
          this.localStorageService.setItem(LOCAL_STORAGE_KEYS.InitialRegion, this.selectedRegion$.value);
          location.reload();
        },
        error: (error) => {
          datadogLogs.logger.error('Failed to update region:', {}, error);
        }
      });
    }
  }
}
